import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { API_BASE_URL } from '../config';
import FilterForm from './filterForm';
import { jwtDecode } from 'jwt-decode';
import StarRating from '../components/StarRating/startRating';
import Loader from '../components/Loader';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";
import styles from './mapsearchV2.module.css'; // Import the new CSS module
import { HardHat, Wrench, ShoppingCart } from 'lucide-react';
import BusinessCard from './BusinessCard';
import BusinessProfilePopup from './BusinessProfilePopup';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const MapComponent = ({ locations, center, activeMarkerIndex, onMarkerClick, onMapLoad }) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    if (!isLoaded) return <div>Loading...</div>;

    // --- To customize the map markers, edit the values below ---

    // Unselected marker style
    const unselectedMarkerIcon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8, // Adjust size of the circle
        fillColor: "#ff0000", // Inner color of the circle
        fillOpacity: 1,
        strokeColor: "white", // Color of the outer ring
        strokeWeight: 3, // Thickness of the outer ring
    };

    // Selected marker style
    const selectedMarkerIcon = {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new window.google.maps.Size(60, 60), // Adjust size of the pin
        labelOrigin: new window.google.maps.Point(30, -15) // Adjust label position relative to the pin
    };

    return (
        <GoogleMap
            onLoad={onMapLoad}
            center={center}
            zoom={13}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
            }}
        >
            {locations.map((location, index) => (
                <Marker
                    key={index}
                    position={{ lat: location.latitude, lng: location.longitude }}
                    onClick={() => onMarkerClick(index)}
                    icon={activeMarkerIndex === index ? selectedMarkerIcon : unselectedMarkerIcon}
                    label={activeMarkerIndex === index ? {
                        text: location.businessName,
                        color: "black",
                        fontWeight: "bold",
                        fontSize: "14px",
                        className: styles.markerLabel
                    } : undefined}
                    zIndex={activeMarkerIndex === index ? 100 : 1}
                />
            ))}
        </GoogleMap>
    );
};

const MapSearchV2 = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const mapRef = useRef(null);

    const onMapLoad = (map) => {
        mapRef.current = map;
    };

    const searchTermFromQuery = query.get('searchTerm');
    const postCodeFromQuery = query.get('postCode');
    const typeFromQuery = query.get('type');

    const [searchTerm, setSearchTerm] = useState(searchTermFromQuery || '');
    const [postCode, setPostCode] = useState(postCodeFromQuery || '');
    const [selectedType, setSelectedType] = useState(typeFromQuery || '');

    const [results, setResults] = useState([]);
    const [professionResults, setProfessionResults] = useState([]);
    const [locations, setLocations] = useState([]);
    const [center, setCenter] = useState({ lat: -33.8688, lng: 151.2093 });
    const [activeMarkerIndex, setActiveMarkerIndex] = useState(null);
    const [selectedBusinessId, setSelectedBusinessId] = useState(null);
    const [selectedBusinessIdForProfile, setSelectedBusinessIdForProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [userType, setUserType] = useState(null);
    //const [selectedType, setSelectedType] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({});
    const [postCodeError, setPostCodeError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decodedToken = jwtDecode(token);
            setUsername(decodedToken.UserName);
            setUserType(decodedToken.UserType);
        }
    }, []);

    useEffect(() => {

        const performSearch = async () => {
            if (searchTermFromQuery && postCodeFromQuery) {
                setLoading(true);
                try {
                    const response = await axios.post(`${API_BASE_URL}/api/SearchLocations/searchFLEXbyLocation`, {
                        searchTerm: searchTermFromQuery,
                        postCode: postCodeFromQuery,
                        type: typeFromQuery,
                    });

                    console.log(response);

                    //if (response.data && response.data.businesses && response.data.businesses.length > 0) {
                    if (response.data.businesses.length > 0) {
                        setResults(response.data.businesses);
                        setLocations(response.data.locations);
                        if (response.data.center) {
                            setCenter({
                                lat: response.data.center.latitude,
                                lng: response.data.center.longitude,
                            });
                        }
                    } else {
                        setResults([]);
                        setLocations([]);
                    }

                    // Clear the type here to enable free text search
                    setSelectedType('');

                } catch (error) {
                    console.error('Error performing search:', error);
                }
                setLoading(false);
            }
        };
        performSearch();
    }, [searchTermFromQuery, postCodeFromQuery, typeFromQuery]);

    const handleSearchChange = async (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);

        if (searchValue.length > 0) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/SearchLocations/searchAll?search=${searchValue}`);
                setProfessionResults(response.data);
            } catch (error) {
                console.error('Error fetching professions:', error);
            }
        } else {
            setProfessionResults([]);
        }
    };

    const addItem = (item) => {
        setSearchTerm(item.name);
        setSelectedType(item.type);
        setProfessionResults([]);
    };

    const handleSearchSubmit = () => {
        if (postCode.trim().length !== 4 || !/^\d{4}$/.test(postCode)) {
            setPostCodeError('Postcode must be a 4-digit number.');
            return;
        }
        setPostCodeError('');
        navigate(`/map-search-v2?searchTerm=${searchTerm}&postCode=${postCode}&type=${selectedType}`);
    };
    
    const handleMarkerClick = (index) => {
        setActiveMarkerIndex(index);
        const businessId = results[index]?.businessId;
        setSelectedBusinessId(businessId || null);
        setSelectedBusinessIdForProfile(businessId || null);
    };

    const handleResultCardClick = (businessId) => {
        const index = results.findIndex(r => r.businessId === businessId);
        if (index !== -1) {
            setActiveMarkerIndex(index);
            setSelectedBusinessId(businessId);
            setSelectedBusinessIdForProfile(businessId);
            const location = locations[index];
            if(location && mapRef.current) {
                mapRef.current.panTo({ lat: location.latitude, lng: location.longitude });
            }
        }
    };

    const handleCloseProfile = () => {
        setSelectedBusinessIdForProfile(null);
    };

    return (
        <div className={styles.pageContainer}>
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'search'} />
            <main className={styles.mainContent}>
                {loading && <Loader />}
                <section className={styles.searchSection}>
                    <div className={styles.searchForm}>
                        <div className={styles.searchInputContainer}>
                            <input
                                type="text"
                                value={postCode}
                                onChange={(e) => setPostCode(e.target.value)}
                                placeholder="Postcode"
                                className={styles.searchInput}
                            />
                            {postCodeError && <p className={styles.errorMessage}>{postCodeError}</p>}
                        </div>
                        <div className={styles.searchInputContainer}>
                            <input
                                type="text"
                                placeholder="Electrician or install air cond.."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                            />
                            {professionResults.length > 0 && (
                                <ul className={styles.dropdownResults}>
                                    {Object.entries(
                                        professionResults.reduce((acc, item) => {
                                            if (!acc[item.type]) {
                                                acc[item.type] = [];
                                            }
                                            acc[item.type].push(item);
                                            return acc;
                                        }, {})
                                    ).map(([type, items]) => (
                                        <React.Fragment key={type}>
                                            <li className={styles.categoryHeader}>Select a {type}</li>
                                            {items.map((item) => (
                                                <li key={`${item.type}-${item.id}`} className={styles.resultItem} onClick={() => addItem(item)}>
                                                    {item.type === 'Profession' && <HardHat className={styles.icon} />}
                                                    {item.type === 'Service' && <Wrench className={styles.icon} />}
                                                    {item.type === 'Product' && <ShoppingCart className={styles.icon} />}
                                                    <span>{item.name}</span>
                                                </li>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)} className={styles.filterButton}>
                            Filters
                        </button>
                        <button onClick={handleSearchSubmit} className={styles.searchButton}>
                            Search
                        </button>
                    </div>
                </section>

                <div className={styles.resultsAndMapContainer}>
                    <div className={styles.resultsPanel} style={{ flex: `0 0 ${selectedBusinessIdForProfile ? '30%' : '35%'}` }}>
                        <h2 className={styles.resultsHeader}>
                            {results.length} results for "{searchTermFromQuery}" in {postCodeFromQuery}
                        </h2>
                        {results.map((result) => (
                            <BusinessCard
                                key={result.businessId}
                                business={result}
                                isSelected={selectedBusinessId === result.businessId}
                                onCardClick={handleResultCardClick}
                            />
                        ))}
                    </div>
                    {selectedBusinessIdForProfile && (
                        <div className={styles.profilePanel} style={{ flex: '0 0 31%' }}>
                            <BusinessProfilePopup
                                key={selectedBusinessIdForProfile}
                                businessId={selectedBusinessIdForProfile}
                                onClose={handleCloseProfile}
                            />
                        </div>
                    )}
                    <div className={styles.mapPanel} style={{ flex: `0 0 ${selectedBusinessIdForProfile ? '39%' : '65%'}` }}>
                        <MapComponent
                            locations={locations}
                            center={center}
                            activeMarkerIndex={activeMarkerIndex}
                            onMarkerClick={handleMarkerClick}
                            onMapLoad={onMapLoad}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
};

export default MapSearchV2;
